/**
 * Job Seeker Subscription Page
 * Premium plan subscription for job seekers
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Star, TrendingUp, Award, Zap } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import RazorpayCheckout from '../../components/Payment/RazorpayCheckout';
import { getPlanDetails } from '../../services/razorpayService';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { firebaseApp } from '../../firebase';
import toast from 'react-hot-toast';

const db = getFirestore(firebaseApp);

const JobSeekerSubscription: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);

  const plan = getPlanDetails('JOBSEEKER');

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.id) return;

      try {
        const userDoc = await getDoc(doc(db, 'jobseekers', user.id));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          setIsPremium(data.isPremium || false);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  const handlePaymentSuccess = () => {
    toast.success('Subscription activated successfully!');
    setIsPremium(true);

    // Redirect to dashboard after 2 seconds
    setTimeout(() => {
      navigate('/job-seeker');
    }, 2000);
  };

  const handlePaymentFailure = (error: any) => {
    toast.error(error.message || 'Payment failed. Please try again.');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (isPremium) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">You're Premium!</h1>
            <p className="text-gray-600 mb-6">
              You already have an active premium subscription. Enjoy all the premium features!
            </p>
            <button
              onClick={() => navigate('/job-seeker')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-800 rounded-full text-sm font-semibold mb-4">
            <Star className="w-4 h-4" />
            Premium Membership
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Unlock Your Career Potential
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get unlimited access to personalized courses, advanced analytics, and priority support
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Features Section */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Premium Features</h2>

              <div className="space-y-4">
                {plan.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-blue-600" />
                    </div>
                    <p className="text-gray-700">{feature}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Benefits */}
            <div className="bg-gradient-to-br from-red-700 to-red-900 rounded-2xl shadow-lg p-8 text-white">
              <h3 className="text-xl font-bold mb-4">Why Go Premium?</h3>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <TrendingUp className="w-6 h-6 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Accelerate Your Learning</p>
                    <p className="text-red-100 text-sm">
                      Get personalized courses tailored to your skill gaps
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Award className="w-6 h-6 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Earn Certificates</p>
                    <p className="text-red-100 text-sm">
                      Showcase your achievements with verified certificates
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Zap className="w-6 h-6 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Priority Support</p>
                    <p className="text-red-100 text-sm">
                      Get faster responses and dedicated assistance
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing & Payment Section */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-blue-200">
              <div className="text-center mb-6">
                <p className="text-gray-600 mb-2">Monthly Subscription</p>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-5xl font-bold text-gray-900">₹{plan.amount}</span>
                  <span className="text-gray-600">/month</span>
                </div>
              </div>

              {/* Payment Component */}
              {user && userData && (
                <RazorpayCheckout
                  amount={plan.amount}
                  planType="JOBSEEKER"
                  planDuration={plan.duration}
                  planName={plan.name}
                  userDetails={{
                    name: userData.name || user.email || 'User',
                    email: userData.email || user.email || '',
                    contact: userData.phone || '',
                  }}
                  onSuccess={handlePaymentSuccess}
                  onFailure={handlePaymentFailure}
                  buttonText="Subscribe Now"
                  buttonClassName="w-full bg-red-800 text-white hover:bg-red-900"
                />
              )}

              {/* Money Back Guarantee */}
              <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                <p className="text-sm text-gray-600">
                  ✓ Cancel anytime
                  <br />
                  ✓ 7-day money back guarantee
                  <br />
                  ✓ No hidden charges
                </p>
              </div>
            </div>

            {/* Testimonial */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-700 mb-3 italic">
                "The personalized courses helped me land my dream job! Best investment I've made in my career."
              </p>
              <p className="text-sm text-gray-600">
                - Priya S., Software Engineer
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-12 bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Frequently Asked Questions
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Can I cancel anytime?</h3>
              <p className="text-gray-600 text-sm">
                Yes, you can cancel your subscription at any time. No questions asked.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">How do I access courses?</h3>
              <p className="text-gray-600 text-sm">
                Once subscribed, courses will be automatically generated based on your test results.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Is my payment secure?</h3>
              <p className="text-gray-600 text-sm">
                Yes, we use Razorpay's secure payment gateway. Your data is encrypted and protected.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">What payment methods are accepted?</h3>
              <p className="text-gray-600 text-sm">
                We accept credit/debit cards, UPI, net banking, and digital wallets.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobSeekerSubscription;
